import { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutSite } from '../components/layout';
import { Header } from '../components/header';
import { batch_start, batch_status, batch_stop, batch_outputs, batch_download } from '../services/api.service';
import { BatchOptions, OutputFile } from '../types';
import './batch.scss';

const STORAGE_KEY = 'batch.options';
const JOB_KEY = 'batch.jobId';      // last job, to re-attach after a reload
const POLL_MS = 1500;
const MAX_LOG_LINES = 5000;

/* -------------------------------------------------------------------------------- *\
|                               appendCapped
\* -------------------------------------------------------------------------------- */
/** Append lines, keeping at most MAX_LOG_LINES (drop the oldest). */
function appendCapped(prev: string[], add: string[]): string[]
{
    const next = prev.concat(add);
    return next.length > MAX_LOG_LINES ? next.slice(next.length - MAX_LOG_LINES) : next;
}

/* -------------------------------------------------------------------------------- *\
|                               levelOf
\* -------------------------------------------------------------------------------- */
function levelOf(line: string): 'error' | 'warn' | 'info'
{
    if (/\berror\b/i.test(line) || /\[erreur\]/i.test(line)) return 'error';
    if (/\bwarn(ing)?\b/i.test(line) || line.startsWith('[!]')) return 'warn';
    return 'info';
}
/* -------------------------------------------------------------------------------- *\
|                               loadOptions
\* -------------------------------------------------------------------------------- */
function loadOptions(): BatchOptions
{
    try
    {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    }
    catch { /* ignore */ }
    return {};
}
/* -------------------------------------------------------------------------------- *\
|                               humanSize
\* -------------------------------------------------------------------------------- */
function humanSize(n: number): string
{
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
/* -------------------------------------------------------------------------------- *\
|                               BatchPage
\* -------------------------------------------------------------------------------- */
export function BatchPage()
{
    const [opts, setOpts] = useState<BatchOptions>(loadOptions());
    const [files, setFiles] = useState<File[]>([]);
    const [jobId, setJobId] = useState<string>('');
    const [running, setRunning] = useState<boolean>(false);
    const [exitCode, setExitCode] = useState<number | null>(null);
    const [lines, setLines] = useState<string[]>([]);
    const [outputs, setOutputs] = useState<OutputFile[]>([]);
    const [error, setError] = useState<string>('');
    const [autoScroll, setAutoScroll] = useState<boolean>(true);

    const fromRef = useRef<number>(0);
    const contentRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Persist options (not files) on every change.
    useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(opts)); }, [opts]);

    // Persist the current job id so a reload can re-attach to it.
    useEffect(() => { if (jobId) localStorage.setItem(JOB_KEY, jobId); }, [jobId]);

    // On mount, re-attach to a previously started job if it still exists.
    useEffect(() =>
    {
        const saved = localStorage.getItem(JOB_KEY);
        if (!saved) return;
        let cancelled = false;
        (async () =>
        {
            try
            {
                const st = await batch_status(saved, 0);
                if (cancelled) return;
                setJobId(saved);
                setLines(st.lines.slice(-MAX_LOG_LINES));
                fromRef.current = st.nextFrom;
                setExitCode(st.exitCode);
                setRunning(st.running);
                if (!st.running)
                {
                    try { setOutputs(await batch_outputs(saved)); } catch { /* ignore */ }
                }
            }
            catch { localStorage.removeItem(JOB_KEY); }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line
    }, []);

    const set = (patch: Partial<BatchOptions>) => setOpts(prev => ({ ...prev, ...patch }));

    /* -------------------------------------------------------------------------------- *\
    |                               poll
    \* -------------------------------------------------------------------------------- */
    const poll = useCallback(async (id: string) =>
    {
        try
        {
            const st = await batch_status(id, fromRef.current);
            if (st.lines.length > 0)
            {
                setLines(prev => appendCapped(prev, st.lines));
                fromRef.current = st.nextFrom;
            }
            setRunning(st.running);
            setExitCode(st.exitCode);

            // On completion, fetch the produced result files.
            if (!st.running)
            {
                try { setOutputs(await batch_outputs(id)); }
                catch { /* ignore */ }
            }
        }
        catch (e: any)
        {
            setError(e?.message || 'status error');
        }
    }, []);

    useEffect(() =>
    {
        if (!jobId || !running) return;
        const interval = setInterval(() => poll(jobId), POLL_MS);
        return () => clearInterval(interval);
    }, [jobId, running, poll]);

    useEffect(() =>
    {
        const el = contentRef.current;
        if (el && autoScroll) el.scrollTop = el.scrollHeight;
    }, [lines, autoScroll]);

    /* -------------------------------------------------------------------------------- *\
    |                               onPickFiles
    \* -------------------------------------------------------------------------------- */
    const onPickFiles = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        const picked = Array.from(e.target.files || [])
            .filter(f => f.name.toLowerCase().endsWith('.tar.gz') || f.name.toLowerCase().endsWith('.tgz'));
        setFiles(picked);
    };
    /* -------------------------------------------------------------------------------- *\
    |                               onRun
    \* -------------------------------------------------------------------------------- */
    const onRun = async () =>
    {
        setError('');
        if (files.length === 0)
        {
            setError("Sélectionnez au moins un fichier .tar.gz.");
            return;
        }
        setLines([]);
        setOutputs([]);
        fromRef.current = 0;
        setExitCode(null);
        try
        {
            const { jobId: id } = await batch_start(files, opts);
            setJobId(id);
            setRunning(true);
            poll(id);
        }
        catch (e: any)
        {
            setError(e?.response?.data?.message || e?.message || 'start error');
        }
    };
    /* -------------------------------------------------------------------------------- *\
    |                               onStop
    \* -------------------------------------------------------------------------------- */
    const onStop = async () =>
    {
        if (!jobId) return;
        try { await batch_stop(jobId); }
        catch (e: any) { setError(e?.message || 'stop error'); }
    };

    return (
        <LayoutSite>
            <div className="batch">
                <Header title="Batch Process" />

                <div className="batch__body">
                    <div className="batch__form">
                        <div className="batch__drop"
                             onClick={() => fileInputRef.current?.click()}>
                            <input ref={fileInputRef} type="file" multiple
                                   accept=".tar.gz,.tgz,application/gzip"
                                   style={{ display: 'none' }}
                                   onChange={onPickFiles} />
                            {files.length === 0
                                ? <span>Cliquer pour choisir des fichiers .tar.gz</span>
                                : <span>{files.length} fichier(s) sélectionné(s)</span>}
                        </div>
                        {files.length > 0 && (
                            <div className="batch__files">
                                {files.map((f, i) => (
                                    <div key={i} className="batch__files__item">
                                        <span className="batch__files__name">{f.name}</span>
                                        <span className="batch__files__size">{humanSize(f.size)}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <label className="batch__field">
                            <span>Start date</span>
                            <input value={opts.startDate || ''}
                                   onChange={e => set({ startDate: e.target.value })}
                                   placeholder="YYYYMMDD" />
                        </label>
                        <label className="batch__field">
                            <span>End date</span>
                            <input value={opts.endDate || ''}
                                   onChange={e => set({ endDate: e.target.value })}
                                   placeholder="YYYYMMDD" />
                        </label>
                        <label className="batch__field">
                            <span>Deploy API key</span>
                            <input value={opts.deploy || ''}
                                   onChange={e => set({ deploy: e.target.value })}
                                   placeholder="(optionnel)" />
                        </label>
                        <label className="batch__field">
                            <span>Turfinfo cache dir</span>
                            <input value={opts.cacheDir || ''}
                                   onChange={e => set({ cacheDir: e.target.value })}
                                   placeholder="(optionnel)" />
                        </label>

                        <div className="batch__checks">
                            <label>
                                <input type="checkbox" checked={!!opts.useCache}
                                       onChange={e => set({ useCache: e.target.checked })} />
                                Utiliser le cache turfinfo
                            </label>
                            <label>
                                <input type="checkbox" checked={!!opts.verbose}
                                       onChange={e => set({ verbose: e.target.checked })} />
                                Verbose
                            </label>
                        </div>

                        <div className="batch__actions">
                            <button className="batch__run" onClick={onRun} disabled={running}>
                                ▶ Run
                            </button>
                            <button className="batch__stop" onClick={onStop} disabled={!running}>
                                ■ Stop
                            </button>
                            <span className={'batch__state ' + (running ? 'batch__state__run' : '')}>
                                {running
                                    ? 'en cours…'
                                    : exitCode === null ? '' : `terminé (code ${exitCode})`}
                            </span>
                        </div>

                        {error && <div className="batch__error">{error}</div>}

                        {outputs.length > 0 && (
                            <div className="batch__outputs">
                                <div className="batch__outputs__title">Fichiers de sortie</div>
                                {outputs.map(o => (
                                    <div key={o.name} className="batch__outputs__item"
                                         onClick={() => batch_download(jobId, o.name)}>
                                        <span className="batch__outputs__name">⬇ {o.name}</span>
                                        <span className="batch__outputs__size">{humanSize(o.size)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="batch__logwrap">
                        <div className="batch__logbar">
                            <label>
                                <input type="checkbox" checked={autoScroll}
                                       onChange={e => setAutoScroll(e.target.checked)} />
                                auto scroll
                            </label>
                            <button onClick={() => setLines([])}>clear</button>
                            <span className="batch__count">{lines.length} lignes</span>
                        </div>
                        <div className="batch__log" ref={contentRef}>
                            {lines.map((line, i) => (
                                <div key={i} className={`batch__line batch__line__${levelOf(line)}`}>{line}</div>
                            ))}
                            {lines.length === 0 && <div className="batch__line batch__line__info">(pas de sortie)</div>}
                        </div>
                    </div>
                </div>
            </div>
        </LayoutSite>
    );
}
